import React from 'react';
import PropTypes from 'prop-types';

import {ButtonBase, TableCell} from 'views/components';
import {ArrowDownwardOutlined, ArrowUpwardOutlined, UnfoldMoreOutlined} from 'views/icons';
import {Translate, translate} from 'views/module';

const headerCellSx = {
  fontSize: '11px',
  fontWeight: 600,
  lineHeight: '14px',
  letterSpacing: '0.04em',
  color: '#A8A49E',
  whiteSpace: 'nowrap',
  borderBottom: '1px solid #EAEAEA',
  px: 1,
};

const renderSortIcon = (isActive, direction) => {
  if (!isActive) {
    return <UnfoldMoreOutlined sx={{fontSize: 14, color: '#C0BBB2'}} />;
  }

  return direction === 'asc' ?
    <ArrowUpwardOutlined sx={{fontSize: 14, color: '#1A1916'}} /> :
    <ArrowDownwardOutlined sx={{fontSize: 14, color: '#1A1916'}} />;
};

// Renders a plain header when the column is not sortable, and an accessible sort toggle
// (aria-sort on the cell plus a descriptive aria-label on the button) otherwise.
const SortableHeaderCell = (props) => {
  const {column, sortState, onSort} = props;

  if (!column.sortKey) {
    return (
      <TableCell align={column.align} sx={headerCellSx}>
        <Translate expression={column.headerExpression} />
      </TableCell>
    );
  }

  const isActive = sortState.key === column.sortKey;
  const columnName = translate(column.headerExpression);
  const directionWord = translate(sortState.direction === 'asc' ?
    'metrics.ClientMetrics.ClientMonthly.Table.SortAscending' :
    'metrics.ClientMetrics.ClientMonthly.Table.SortDescending');
  const ariaLabel = isActive ?
    translate('metrics.ClientMetrics.ClientMonthly.Table.SortActive', {column: columnName, direction: directionWord}) :
    translate('metrics.ClientMetrics.ClientMonthly.Table.SortInactive', {column: columnName});

  return (
    <TableCell
      align={column.align}
      sortDirection={isActive ? sortState.direction : false}
      sx={headerCellSx}
    >
      <ButtonBase
        onClick={() => onSort(column.sortKey)}
        aria-label={ariaLabel}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          color: isActive ? '#1A1916' : '#A8A49E',
          fontSize: '11px',
          fontWeight: 600,
          letterSpacing: '0.04em',
          borderRadius: '4px',
        }}
      >
        <Translate expression={column.headerExpression} />
        {renderSortIcon(isActive, sortState.direction)}
      </ButtonBase>
    </TableCell>
  );
};

SortableHeaderCell.propTypes = {
  column: PropTypes.shape({
    align: PropTypes.string,
    sortKey: PropTypes.string,
    headerExpression: PropTypes.string.isRequired,
  }).isRequired,
  sortState: PropTypes.shape({
    key: PropTypes.string.isRequired,
    direction: PropTypes.oneOf(['asc', 'desc']).isRequired,
  }).isRequired,
  onSort: PropTypes.func.isRequired,
};

export default React.memo(SortableHeaderCell);
